import { useAutocompleteStyles } from '@app/src/components/Form/Select/SimpleSelect'
import TextField from '@app/src/components/Ui/TextField'
import { useSearchProviders } from '@app/src/pages/TabularImport/shared/tabularImportHooks'
import { ProductMappingStatus, ProductStatus } from '@app/src/types/product'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Autocomplete, MenuItem } from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useDebounce } from 'use-debounce'

const SEARCH_DEBOUNCE_MS = 500

type ProviderEditorProps = {
  value: string
  disabled: boolean
  required?: boolean
  onChange: (displayValue: string, providerId: number | null) => void
}

// Plain MUI controls: the form-page editors (ProviderField, ProductStatusField, ...) need a
// react-hook-form context the review table does not have.
export const ProviderEditor: React.FC<ProviderEditorProps> = ({ value, disabled, required, onChange }) => {
  const classes = useAutocompleteStyles()
  const [query, setQuery] = useState(value)
  const [debouncedQuery] = useDebounce(query, SEARCH_DEBOUNCE_MS)
  // No minimum length: a row lands here precisely because its supplier is missing or unmatched,
  // so the organization's providers must be browsable before anything is typed.
  const { items: results, isFetching } = useSearchProviders(debouncedQuery, !disabled)
  const showRequiredError = Boolean(required) && !value

  return (
    // Not freeSolo: typed text is only a search query. A name that was never picked from the list
    // has no provider id behind it, and the row would look valid here and fail at import.
    <Autocomplete<{ id: number; name: string }, false, false, false>
      size="small"
      options={results}
      getOptionLabel={o => o.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      inputValue={query}
      // MUI blanks the input on blur when nothing was picked. Show the stored supplier again
      // instead, so the cell never reads as empty while the record still carries a supplier.
      onInputChange={(_, v, reason) => setQuery(reason === 'reset' && !v ? value : v)}
      onChange={(_, v) => onChange(v?.name ?? '', v?.id ?? null)}
      filterOptions={x => x}
      // Without this the debounce window renders as "No options", which reads as "this
      // organization has no suppliers" rather than "still typing".
      loading={isFetching || query !== debouncedQuery}
      disabled={disabled}
      popupIcon={<ExpandMoreIcon />}
      classes={{ clearIndicator: classes.clearIndicator, popupIndicator: classes.popupIndicator, paper: classes.paper }}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          name="providerEditor"
          required={required}
          error={showRequiredError}
          helperText={showRequiredError ? 'Required' : ''}
        />
      )}
      sx={{ minWidth: 180 }}
    />
  )
}

type EnumEditorProps = {
  value: string
  disabled: boolean
  options: { value: string; labelId: string }[]
  onChange: (value: string) => void
}

const EnumEditor: React.FC<EnumEditorProps> = ({ value, disabled, options, onChange }) => {
  const { formatMessage } = useIntl()

  return (
    <TextField
      select
      variant="outlined"
      size="small"
      fullWidth
      value={value}
      disabled={disabled}
      onChange={e => onChange(e.target.value)}
    >
      {/* Blank is distinct from NotSelected: it means the import carries no value for this
          field, so the import leaves the existing one alone. */}
      <MenuItem value="">—</MenuItem>
      {options.map(o => (
        <MenuItem key={o.value} value={o.value}>
          {formatMessage({ id: o.labelId })}
        </MenuItem>
      ))}
    </TextField>
  )
}

// Enum values are stored as enum names (Active, PartlyMapped), which ProductFieldParsing accepts.
export const ProductStatusEditor: React.FC<Omit<EnumEditorProps, 'options'>> = props => (
  <EnumEditor
    {...props}
    options={[ProductStatus.NotSelected, ProductStatus.Active, ProductStatus.Inactive].map(s => ({
      value: s,
      labelId: `schemas.product.productStatusValues.${s}`,
    }))}
  />
)

export const MappingStatusEditor: React.FC<Omit<EnumEditorProps, 'options'>> = props => (
  <EnumEditor
    {...props}
    options={[
      ProductMappingStatus.NotSelected,
      ProductMappingStatus.NotMapped,
      ProductMappingStatus.PartlyMapped,
      ProductMappingStatus.FullyMapped,
    ].map(s => ({ value: s, labelId: `schemas.product.mappingStatusValues.${s}` }))}
  />
)
